import type { DeviceCapabilities } from '../device.js';
import { keyCapHtml } from '../input-glyphs.js';
import {
  KEY_ACTION_LABELS,
  isReservedKeyCode,
  keyboardBindings,
  resetKeyboardBindings,
  setKeyboardBinding,
  type KeyAction,
} from '../keyboard-bindings.js';
import type { GameSettings, SteeringAssistMode } from '../settings.js';
import { escapeHtml } from '../ui/html.js';
import { t } from '../i18n/index.js';
import {
  TiltPreview,
  motionAccess,
  motionNeedsPermissionTap,
  requestMotionAccess,
  type MotionAccess,
} from '../motion-preview.js';

export type SteeringDevice = 'keyboard' | 'gamepad' | 'touch' | 'tilt';

export interface DriverStepModel {
  name: string;
  settings: GameSettings;
  capabilities: DeviceCapabilities;
  device: SteeringDevice;
  gamepad: { connected: boolean; name: string };
  invite: { hostName: string; trackName: string } | null;
}

export interface DriverStepHost {
  settings(): GameSettings;
  saveSettings(settings: GameSettings): void;
  setName(name: string): void;
  suggestName(): string;
  setDevice(device: SteeringDevice): void;
  next(): void;
  toast(message: string): void;
}

const KEY_ROWS: readonly KeyAction[] = ['throttle', 'brake', 'left', 'right', 'handbrake', 'nitro', 'reset', 'camera'];
const ASSISTS: readonly { id: SteeringAssistMode; label: string; detail: string }[] = [
  { id: 'arcade', label: 'ARCADE', detail: 'Forgiving near the limit. Great for touch and first races.' },
  { id: 'balanced', label: 'BALANCED', detail: 'Subtle help that stays out of the way.' },
  { id: 'pro', label: 'PRO', detail: 'Raw input. Every correction is yours.' },
];

export function preferredSteeringDevice(
  capabilities: DeviceCapabilities,
  settings: GameSettings,
  gamepadConnected: boolean,
): SteeringDevice {
  if (gamepadConnected) return 'gamepad';
  if (capabilities.touchDevice) return capabilities.motionSteering && settings.motionSteeringEnabled ? 'tilt' : 'touch';
  return 'keyboard';
}

/** Line icons instead of emoji: emoji render as colourful stickers on phones. */
const DEVICE_ICONS = {
  keyboard: '<svg viewBox="0 0 24 24"><rect x="2.5" y="6" width="19" height="12" rx="2"/><path d="M6 10h1M9.5 10h1M13 10h1M16.5 10h1M7 14h10"/></svg>',
  gamepad: '<svg viewBox="0 0 24 24"><path d="M7.5 7h9a5 5 0 0 1 4.9 6l-.7 3.4a2.4 2.4 0 0 1-4.1 1.2L14.5 15h-5l-2.1 2.6a2.4 2.4 0 0 1-4.1-1.2L2.6 13a5 5 0 0 1 4.9-6Z"/><path d="M8 9.8v3.4M6.3 11.5h3.4M15.5 10.5h.01M17.5 12.5h.01"/></svg>',
  touch: '<svg viewBox="0 0 24 24"><path d="M9 11V4.8a1.8 1.8 0 0 1 3.6 0V11l4.6.9a2.4 2.4 0 0 1 1.9 2.7l-.7 4.4a2.4 2.4 0 0 1-2.4 2H11a2.4 2.4 0 0 1-1.9-.9l-3.6-4.6a1.6 1.6 0 0 1 2.4-2.1L9 14.5"/></svg>',
  tilt: '<svg viewBox="0 0 24 24"><rect x="8" y="3.5" width="8" height="17" rx="2" transform="rotate(-18 12 12)"/><path d="M3 9.5a9 9 0 0 0 0 5M21 9.5a9 9 0 0 1 0 5"/></svg>',
} as const;

export function driverStepHtml(model: DriverStepModel): string {
  const valid = model.name.trim().length >= 2;
  const touch = model.capabilities.touchDevice;
  const allDevices: { id: SteeringDevice; label: string; detail: string; icon: string; available: boolean; detected: boolean }[] = [
    { id: 'keyboard', label: 'KEYBOARD', detail: t('WASD / arrows, fully remappable'), icon: DEVICE_ICONS.keyboard, available: true, detected: !touch },
    { id: 'gamepad', label: 'CONTROLLER', detail: model.gamepad.connected ? model.gamepad.name || t('Connected') : t('Press any button to connect'), icon: DEVICE_ICONS.gamepad, available: true, detected: model.gamepad.connected },
    { id: 'touch', label: 'TOUCH', detail: t('On-screen thumb controls'), icon: DEVICE_ICONS.touch, available: touch, detected: touch && !model.capabilities.motionSteering },
    { id: 'tilt', label: 'TILT', detail: t('Steer by tilting your phone'), icon: DEVICE_ICONS.tilt, available: model.capabilities.motionSteering, detected: model.capabilities.motionSteering },
  ];
  // Phones and tablets lead with the motion sensor and drop the keyboard
  // card; a desktop never shows the touch-only options first.
  const devices = touch
    ? (['tilt', 'touch', 'gamepad'] as const).map((id) => allDevices.find((device) => device.id === id)!)
    : allDevices;
  return `
    <div class="driver-step">
      <section class="driver-identity" aria-labelledby="driver-title">
        ${model.invite ? `<aside class="wizard-invite-banner"><i aria-hidden="true">↗</i><span><small>${t("YOU'RE INVITED")}</small><b>${t("{name}'s lobby · {track}", { name: escapeHtml(model.invite.hostName), track: escapeHtml(model.invite.trackName) })}</b><em>${t('Set up your driver and car, then you join straight in.')}</em></span></aside>` : ''}
        <p class="eyebrow">${t('STEP 2 OF 6')}</p>
        <h1 id="driver-title">${t("WHO'S")}<br><em>${t('DRIVING?')}</em></h1>
        <p class="wizard-lede">${t('Your callsign shows on the grid, the leaderboard and in every lobby. It stays on this device — no account needed.')}</p>
        <label class="callsign-field" for="driver-name">
          <span class="callsign-label">${t('CALLSIGN')}</span>
          <span class="callsign-input"><i aria-hidden="true">///</i><input id="driver-name" name="name" maxlength="18" autocomplete="nickname" spellcheck="false" value="${escapeHtml(model.name)}" placeholder="${t('YOUR NAME')}" required aria-describedby="callsign-help" data-pad-default><button type="button" class="callsign-suggest" data-driver-action="suggest" title="${t('Suggest a callsign')}" aria-label="${t('Suggest a callsign')}">⟳</button></span>
          <small id="callsign-help" data-valid="${valid}">${valid ? t('2–18 characters · looks good') : t('Use at least two characters')}</small>
        </label>
      </section>

      <section class="driver-steering" aria-labelledby="steering-title">
        <label class="wizard-check driver-view-check"><input type="checkbox" data-setting="cockpitView" ${model.settings.cockpitView ? 'checked' : ''}><i aria-hidden="true"><svg viewBox="0 0 24 24"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg></i><span><b>${t('COCKPIT VIEW')}</b><small>${model.capabilities.touchDevice ? t("Race from the driver's seat. Switch any time with the wheel button, top right.") : t("Race from the driver's seat. Switch any time with the camera key.")}</small></span></label>
        <header class="wizard-section-head"><h2 id="steering-title">${t('STEERING')}</h2><small>${t('Used for every race · change any time from the pause menu')}</small></header>
        <div class="steering-devices" role="radiogroup" aria-label="${t('Steering input')}">
          ${devices.map((device) => `
            <button type="button" role="radio" data-device-icon="${device.id}" class="steering-device ${device.id === model.device ? 'active' : ''}" aria-checked="${device.id === model.device}" data-steering-device="${device.id}" ${device.available ? '' : 'disabled'}>
              <i aria-hidden="true">${device.icon}</i>
              <b>${t(device.label)}</b>
              <small>${device.available ? escapeHtml(device.detail) : t('Not available on this device')}</small>
              ${device.detected && device.available ? `<em>${t('DETECTED')}</em>` : ''}
            </button>`).join('')}
        </div>
        <div class="steering-panel" id="steering-panel">${steeringPanelHtml(model)}</div>
        <div class="steering-assist">
          <span class="wizard-field-label">${t('HANDLING ASSIST')}</span>
          <div class="wizard-segmented" role="radiogroup" aria-label="${t('Handling assist')}">
            ${ASSISTS.map((assist) => `<button type="button" role="radio" aria-checked="${assist.id === model.settings.steeringAssistMode}" class="${assist.id === model.settings.steeringAssistMode ? 'active' : ''}" data-assist="${assist.id}" title="${escapeHtml(t(assist.detail))}"><b>${t(assist.label)}</b><small>${escapeHtml(t(assist.detail))}</small></button>`).join('')}
          </div>
          <label class="wizard-toggle"><input type="checkbox" data-setting="drivingCoach" ${model.settings.drivingCoach ? 'checked' : ''}><i aria-hidden="true"></i><span><b>${t('CORNER COACH')}</b><small>${t('Brake and lift cues before demanding corners.')}</small></span></label>
        </div>
      </section>
    </div>`;
}

function steeringPanelHtml(model: DriverStepModel): string {
  const settings = model.settings;
  switch (model.device) {
    case 'keyboard': {
      const bindings = keyboardBindings();
      return `
        <div class="key-map" role="list" aria-label="${t('Keyboard map')}">
          ${KEY_ROWS.map((action) => `<div class="key-map-row" role="listitem"><span>${t(KEY_ACTION_LABELS[action])}</span><button type="button" class="key-capture" data-rebind="${action}" aria-label="${t('Rebind {action}, currently {key}', { action: t(KEY_ACTION_LABELS[action]).toLowerCase(), key: escapeHtml(bindings[action].primary) })}">${keyCapHtml(bindings[action].primary)}${bindings[action].secondary ? `<small>${t('or')}</small>${keyCapHtml(bindings[action].secondary)}` : ''}</button></div>`).join('')}
        </div>
        <div class="steering-panel-foot"><small>${t('Click a key to rebind it. Esc cancels. Keys already in use swap places.')}</small><button type="button" class="wizard-link" data-driver-action="reset-keys">${t('RESET KEYS')}</button></div>`;
    }
    case 'gamepad':
      return `
        <div class="pad-status" data-connected="${model.gamepad.connected}"><i aria-hidden="true"></i><span><b>${model.gamepad.connected ? escapeHtml(model.gamepad.name || t('CONTROLLER CONNECTED')) : t('NO CONTROLLER YET')}</b><small>${model.gamepad.connected ? t('Triggers drive, left stick steers. Full remapping lives in the pause menu.') : t('Connect a pad and press any button — it is picked up instantly.')}</small></span></div>
        ${rangeHtml('gamepadSteerDeadZone', 'STICK DEAD ZONE', settings.gamepadSteerDeadZone, 0, 0.4, 0.01, (value) => `${Math.round(value * 100)}%`)}
        ${rangeHtml('gamepadSteerLinearity', 'STEERING RESPONSE', settings.gamepadSteerLinearity, 0.5, 2.2, 0.05, (value) => value < 0.9 ? t('QUICK') : value > 1.15 ? t('PRECISE') : t('LINEAR'))}
        ${rangeHtml('gamepadVibration', 'VIBRATION', settings.gamepadVibration, 0, 1, 0.05, (value) => `${Math.round(value * 100)}%`)}`;
    case 'touch':
      return `
        <label class="wizard-select"><span><b>${t('THUMB LAYOUT')}</b><small>${t('Swap steering and pedals for left-handed play.')}</small></span><select data-setting="mobileControlLayout"><option value="standard" ${settings.mobileControlLayout === 'standard' ? 'selected' : ''}>${t('STANDARD')}</option><option value="left-handed" ${settings.mobileControlLayout === 'left-handed' ? 'selected' : ''}>${t('LEFT-HANDED')}</option></select></label>
        ${rangeHtml('mobileControlSize', 'BUTTON SIZE', settings.mobileControlSize, 0.86, 1.08, 0.02, (value) => `${Math.round(value * 100)}%`)}
        <label class="wizard-toggle"><input type="checkbox" data-setting="mobileAutoThrottle" ${settings.mobileAutoThrottle ? 'checked' : ''}><i aria-hidden="true"></i><span><b>${t('AUTO-GAS')}</b><small>${t('Keeps accelerating until you brake.')}</small></span></label>
        <label class="wizard-toggle"><input type="checkbox" data-setting="mobileHaptics" ${settings.mobileHaptics ? 'checked' : ''}><i aria-hidden="true"></i><span><b>${t('HAPTICS')}</b><small>${t('Short pulses on supported phones.')}</small></span></label>`;
    case 'tilt':
      return `
        ${tiltLabHtml(motionAccess())}
        ${rangeHtml('motionSensitivity', 'STEERING RANGE', settings.motionSensitivity, 0.6, 1.8, 0.1, (value) => `${Math.round(24 / Math.sqrt(value))}°`)}
        ${rangeHtml('motionDeadZone', 'NEUTRAL DEAD ZONE', settings.motionDeadZone, 1.2, 6, 0.2, (value) => `${value.toFixed(1)}°`)}
        <label class="wizard-select"><span><b>${t('TILT RESPONSE')}</b><small>${t('Precise softens small movements; Agile reacts earlier.')}</small></span><select data-setting="motionResponseCurve"><option value="precise" ${settings.motionResponseCurve === 'precise' ? 'selected' : ''}>${t('PRECISE')}</option><option value="linear" ${settings.motionResponseCurve === 'linear' ? 'selected' : ''}>${t('LINEAR')}</option><option value="agile" ${settings.motionResponseCurve === 'agile' ? 'selected' : ''}>${t('AGILE')}</option></select></label>`;
  }
}

const TILT_COPY: Record<MotionAccess | 'live' | 'waiting' | 'stalled', { title: string; detail: string }> = {
  unknown: { title: 'WAKE THE SENSOR', detail: 'One tap lets the browser share motion data. You only do this once.' },
  waiting: { title: 'LISTENING…', detail: 'Hold your phone like a steering wheel and give it a little tilt.' },
  live: { title: 'SENSOR LIVE', detail: 'Hold it how you want to race and tap SET CENTRE.' },
  granted: { title: 'SENSOR READY', detail: 'Hold it how you want to race and tap SET CENTRE.' },
  stalled: { title: 'NO SENSOR DATA', detail: 'This browser did not deliver motion data. Touch controls take over in the race.' },
  denied: { title: 'MOTION BLOCKED', detail: 'Allow motion & orientation for this site in the browser settings, then try again.' },
  insecure: { title: 'NEEDS HTTPS', detail: 'Motion sensors only work over a secure connection. Touch controls stay available.' },
  missing: { title: 'NO MOTION SENSOR', detail: 'This device does not report tilt. Touch controls are the way to go.' },
};

type TiltState = keyof typeof TILT_COPY;

function tiltStateFor(access: MotionAccess): TiltState {
  if (access === 'unknown') return motionNeedsPermissionTap() ? 'unknown' : 'waiting';
  return access === 'granted' ? 'waiting' : access;
}

const TILT_BUTTONS: Record<'enable' | 'centre' | 'touch', readonly TiltState[]> = {
  enable: ['unknown', 'denied'],
  centre: ['waiting', 'live', 'granted'],
  touch: ['denied', 'insecure', 'missing', 'stalled'],
};

function tiltLabHtml(access: MotionAccess): string {
  const state = tiltStateFor(access);
  const copy = TILT_COPY[state];
  const hidden = (button: keyof typeof TILT_BUTTONS): string => TILT_BUTTONS[button].includes(state) ? '' : 'hidden';
  return `
    <div class="tilt-lab" id="tilt-lab" data-state="${state}" style="--tilt-steer:0">
      <div class="tilt-wheel" aria-hidden="true">
        <svg viewBox="0 0 120 120">
          <circle class="tilt-wheel-rim" cx="60" cy="60" r="50"/>
          <path class="tilt-wheel-spoke" d="M12 62h32l8 11h16l8-11h32M60 73v37"/>
          <circle class="tilt-wheel-hub" cx="60" cy="62" r="10"/>
          <rect class="tilt-wheel-mark" x="56" y="6" width="8" height="12" rx="2"/>
        </svg>
        <b data-tilt-readout>${t('CENTRE')}</b>
      </div>
      <div class="tilt-lab-body">
        <span class="tilt-lab-chip"><i aria-hidden="true"></i><span data-tilt-title>${t(copy.title)}</span></span>
        <p data-tilt-detail>${t(copy.detail)}</p>
        <div class="tilt-meter" aria-hidden="true"><span>L</span><div><i></i><b></b></div><span>R</span></div>
        <div class="tilt-lab-actions">
          <button type="button" class="tilt-primary" data-driver-action="tilt-enable" ${hidden('enable')}>${state === 'denied' ? t('TRY AGAIN') : t('ENABLE MOTION SENSOR')}</button>
          <button type="button" class="tilt-centre" data-driver-action="tilt-centre" ${hidden('centre')}>${t('SET CENTRE')}</button>
          <button type="button" class="wizard-link" data-driver-action="tilt-touch" ${hidden('touch')}>${t('USE TOUCH INSTEAD')}</button>
        </div>
      </div>
    </div>`;
}

function paintTiltLab(lab: HTMLElement, state: TiltState): void {
  if (lab.dataset.state === state) return;
  lab.dataset.state = state;
  const copy = TILT_COPY[state];
  const title = lab.querySelector<HTMLElement>('[data-tilt-title]');
  const detail = lab.querySelector<HTMLElement>('[data-tilt-detail]');
  if (title) title.textContent = t(copy.title);
  if (detail) detail.textContent = t(copy.detail);
  for (const [button, states] of Object.entries(TILT_BUTTONS)) {
    const element = lab.querySelector<HTMLButtonElement>(`[data-driver-action="tilt-${button}"]`);
    if (element) element.hidden = !states.includes(state);
  }
  const enable = lab.querySelector<HTMLButtonElement>('[data-driver-action="tilt-enable"]');
  if (enable) enable.textContent = state === 'denied' ? t('TRY AGAIN') : t('ENABLE MOTION SENSOR');
}

function rangeHtml(
  key: keyof GameSettings,
  label: string,
  value: number,
  min: number,
  max: number,
  step: number,
  format: (value: number) => string,
): string {
  return `<label class="wizard-range"><span><b>${t(label)}</b><output data-output="${key}">${format(value)}</output></span><input type="range" data-setting="${key}" data-format="${key}" min="${min}" max="${max}" step="${step}" value="${value}"></label>`;
}

const RANGE_FORMATS: Partial<Record<keyof GameSettings, (value: number) => string>> = {
  gamepadSteerDeadZone: (value) => `${Math.round(value * 100)}%`,
  gamepadSteerLinearity: (value) => value < 0.9 ? t('QUICK') : value > 1.15 ? t('PRECISE') : t('LINEAR'),
  gamepadVibration: (value) => `${Math.round(value * 100)}%`,
  mobileControlSize: (value) => `${Math.round(value * 100)}%`,
  motionSensitivity: (value) => `${Math.round(24 / Math.sqrt(value))}°`,
  motionDeadZone: (value) => `${value.toFixed(1)}°`,
};

/** Wires step 1. Returns a disposer for the global key-capture listener. */
export function bindDriverStep(root: HTMLElement, model: DriverStepModel, host: DriverStepHost): () => void {
  const nameInput = root.querySelector<HTMLInputElement>('#driver-name');
  const help = root.querySelector<HTMLElement>('#callsign-help');
  // Only a name the driver typed or picked is saved; the initial suggestion
  // must not count as a finished profile (that would skip this step later).
  const syncName = (persist = true): void => {
    const value = nameInput?.value.trim() ?? '';
    const valid = value.length >= 2;
    if (help) {
      help.dataset.valid = String(valid);
      help.textContent = valid ? t('2–18 characters · looks good') : t('Use at least two characters');
    }
    for (const next of root.querySelectorAll<HTMLButtonElement>('[data-wizard-action="driver-next"]')) next.disabled = !valid;
    if (valid && persist) host.setName(value);
  };
  nameInput?.addEventListener('input', () => syncName());
  nameInput?.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && (nameInput.value.trim().length >= 2)) {
      event.preventDefault();
      host.next();
    }
  });
  let capture: { action: KeyAction; button: HTMLButtonElement } | null = null;
  const stopCapture = (): void => {
    if (!capture) return;
    capture.button.classList.remove('capturing');
    capture = null;
    rerenderPanel();
  };
  const rerenderPanel = (): void => {
    const panel = root.querySelector<HTMLElement>('#steering-panel');
    if (panel) panel.innerHTML = steeringPanelHtml({ ...model, settings: host.settings() });
    syncTilt();
  };

  // Live tilt preview: the sensor runs only while the tilt panel is visible.
  let preview: TiltPreview | null = null;
  let centredFlashUntil = 0;
  const tiltLab = (): HTMLElement | null => root.querySelector<HTMLElement>('#tilt-lab');
  const syncTilt = (): void => {
    const lab = tiltLab();
    const state = tiltStateFor(motionAccess());
    if (!lab || state !== 'waiting') {
      preview?.stop();
      return;
    }
    preview ??= new TiltPreview((sample) => {
      const current = tiltLab();
      if (!current) return;
      if (current.dataset.state === 'waiting' || current.dataset.state === 'stalled') paintTiltLab(current, 'live');
      current.style.setProperty('--tilt-steer', sample.steer.toFixed(3));
      const readout = current.querySelector<HTMLElement>('[data-tilt-readout]');
      const percent = Math.round(Math.abs(sample.steer) * 100);
      if (readout && performance.now() > centredFlashUntil) {
        readout.textContent = percent === 0 ? t('CENTRE') : sample.steer > 0 ? t('{percent}% LEFT', { percent }) : t('{percent}% RIGHT', { percent });
      }
    }, () => {
      const current = tiltLab();
      if (current?.dataset.state === 'waiting') paintTiltLab(current, 'stalled');
    });
    const settings = host.settings();
    preview.sensitivity = settings.motionSensitivity;
    preview.deadZone = settings.motionDeadZone;
    preview.start();
  };
  const enableTilt = (): void => {
    // Called straight from the tap so the browser accepts the permission request.
    void requestMotionAccess().then((access) => {
      const lab = tiltLab();
      if (lab) paintTiltLab(lab, tiltStateFor(access));
      if (access === 'granted') navigator.vibrate?.(16);
      syncTilt();
    });
  };
  const onCaptureKey = (event: KeyboardEvent): void => {
    if (!capture) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.code === 'Escape') {
      stopCapture();
      return;
    }
    if (isReservedKeyCode(event.code)) {
      host.toast(t('{key} is reserved for menus', { key: event.code.replace(/^Key/, '') }));
      return;
    }
    setKeyboardBinding(capture.action, event.code);
    stopCapture();
  };
  window.addEventListener('keydown', onCaptureKey, true);

  root.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    const device = target.closest<HTMLButtonElement>('[data-steering-device]');
    if (device && !device.disabled) {
      const id = device.dataset.steeringDevice as SteeringDevice;
      model.device = id;
      host.setDevice(id);
      for (const button of root.querySelectorAll<HTMLButtonElement>('[data-steering-device]')) {
        const active = button === device;
        button.classList.toggle('active', active);
        button.setAttribute('aria-checked', String(active));
      }
      rerenderPanel();
      if (id === 'tilt' && motionAccess() === 'unknown' && motionNeedsPermissionTap()) enableTilt();
      return;
    }
    const assist = target.closest<HTMLButtonElement>('[data-assist]');
    if (assist) {
      const settings = host.settings();
      settings.steeringAssistMode = assist.dataset.assist as SteeringAssistMode;
      settings.mobileSteeringAssist = settings.steeringAssistMode !== 'pro';
      host.saveSettings(settings);
      for (const button of root.querySelectorAll<HTMLButtonElement>('[data-assist]')) {
        const active = button === assist;
        button.classList.toggle('active', active);
        button.setAttribute('aria-checked', String(active));
      }
      return;
    }
    const rebind = target.closest<HTMLButtonElement>('[data-rebind]');
    if (rebind) {
      if (capture) stopCapture();
      const fresh = root.querySelector<HTMLButtonElement>(`[data-rebind="${rebind.dataset.rebind}"]`) ?? rebind;
      capture = { action: fresh.dataset.rebind as KeyAction, button: fresh };
      fresh.classList.add('capturing');
      fresh.innerHTML = `<em>${t('PRESS A KEY…')}</em>`;
      fresh.focus();
      return;
    }
    const action = target.closest<HTMLElement>('[data-driver-action]')?.dataset.driverAction;
    if (action === 'suggest' && nameInput) {
      nameInput.value = host.suggestName();
      syncName();
      nameInput.focus();
    } else if (action === 'tilt-enable') {
      enableTilt();
    } else if (action === 'tilt-centre') {
      const lab = tiltLab();
      if (preview?.calibrate() && lab) {
        centredFlashUntil = performance.now() + 1_200;
        const readout = lab.querySelector<HTMLElement>('[data-tilt-readout]');
        if (readout) readout.textContent = t('CENTRE SET');
        lab.classList.remove('centred');
        void lab.offsetWidth;
        lab.classList.add('centred');
        navigator.vibrate?.([10, 40, 10]);
      }
    } else if (action === 'tilt-touch') {
      root.querySelector<HTMLButtonElement>('[data-steering-device="touch"]')?.click();
    } else if (action === 'reset-keys') {
      resetKeyboardBindings();
      rerenderPanel();
      host.toast(t('Keyboard map reset to defaults'));
    }
  });
  const onSetting = (event: Event): void => {
    const input = event.target as HTMLInputElement | HTMLSelectElement;
    const key = input.dataset.setting as keyof GameSettings | undefined;
    if (!key) return;
    const settings = host.settings() as unknown as Record<string, unknown>;
    if (input instanceof HTMLInputElement && input.type === 'checkbox') settings[key] = input.checked;
    else if (input instanceof HTMLInputElement && input.type === 'range') {
      const value = Number(input.value);
      settings[key] = value;
      const output = root.querySelector<HTMLOutputElement>(`[data-output="${key}"]`);
      const format = RANGE_FORMATS[key];
      if (output && format) output.textContent = format(value);
      if (preview && key === 'motionSensitivity') preview.sensitivity = value;
      if (preview && key === 'motionDeadZone') preview.deadZone = value;
    } else settings[key] = input.value;
    host.saveSettings(host.settings());
  };
  root.addEventListener('input', onSetting);
  root.addEventListener('change', (event) => {
    if ((event.target as HTMLInputElement).type !== 'range') onSetting(event);
  });
  syncName(false);
  syncTilt();
  return () => {
    window.removeEventListener('keydown', onCaptureKey, true);
    preview?.stop();
  };
}
